import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { StripeElements } from '@stripe/stripe-js'
import { IconLoader2, IconLock } from '@tabler/icons-react'
import { getStripe } from '../../lib/stripe'
import { startCardSetup } from '../../lib/api'
import { translate, useI18n } from '../../lib/i18n'

/**
 * Alta de tarjeta, dentro de nuestra propia página.
 *
 * El formulario es el Payment Element de Stripe: los datos de la tarjeta viajan
 * del navegador a Stripe sin pasar por nosotros ni un instante, que es lo que
 * mantiene esto fuera del alcance de PCI. Lo nuestro es sólo el permiso —el
 * SetupIntent que abre el servidor— y el hueco donde se monta.
 *
 * Se confirma con `redirect: 'if_required'`: una tarjeta normal se guarda aquí
 * mismo, y sólo si el banco pide autenticación aparece su diálogo. El
 * `return_url` está por si algún método sí necesita salir de la página; con
 * tarjeta no llega a usarse.
 */
export function AddCardDialog({ token, onClose, onSaved }: {
  token: string
  onClose: () => void
  /** La tarjeta ya está en Stripe: quien llame recarga su lista. */
  onSaved: () => void
}) {
  const { t } = useI18n()
  const mountRef = useRef<HTMLDivElement>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const [ready, setReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const loader = getStripe()
      if (!loader) { setError(translate('addCard.missingKey')); return }
      const [stripe, setup] = await Promise.all([loader, startCardSetup(token)])
      if (cancelled || !stripe) return

      /* El Element se pinta con el tema del panel, no con el suyo: una tarjeta
         blanca dentro de un diálogo oscuro se ve como un error, no como un
         formulario. */
      const dark = document.documentElement.getAttribute('data-theme') === 'dark'
      const elements = stripe.elements({
        clientSecret: setup.clientSecret,
        appearance: {
          theme: dark ? 'night' : 'stripe',
          variables: { borderRadius: '10px', fontSizeBase: '14px' },
        },
      })
      const payment = elements.create('payment', { layout: 'tabs' })
      if (cancelled || !mountRef.current) return
      payment.mount(mountRef.current)
      payment.on('ready', () => { if (!cancelled) setReady(true) })
      elementsRef.current = elements
    }

    run().catch((cause: unknown) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : translate('addCard.openFailed'))
    })

    return () => {
      cancelled = true
      elementsRef.current = null
    }
  }, [token])

  const save = async () => {
    const elements = elementsRef.current
    if (!elements || saving) return
    const loader = getStripe()
    if (!loader) return
    setSaving(true)
    setError(null)
    try {
      const stripe = await loader
      if (!stripe) throw new Error(t('addCard.stripeUnavailable'))
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: `${window.location.origin}/?view=billing` },
        redirect: 'if_required',
      })
      if (result.error) {
        /* El mensaje de Stripe ya viene traducido y dice lo que pasa de verdad
           ("tu tarjeta fue rechazada"), así que se enseña tal cual. */
        setError(result.error.message ?? t('addCard.saveFailed'))
        setSaving(false)
        return
      }
      onSaved()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('addCard.saveFailed'))
      setSaving(false)
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, saving])

  return createPortal(
    <div
      role="presentation"
      onClick={() => { if (!saving) onClose() }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', display: 'grid', placeItems: 'center', padding: 20 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('addCard.title')}
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(100%, 460px)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--dash-panel)', border: '1px solid var(--dash-border)', borderRadius: 16, boxShadow: '0 24px 70px rgba(0,0,0,0.25)', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div>
          <h3 className="dash-card-title" style={{ margin: '0 0 4px' }}>{t('addCard.title')}</h3>
          <p className="dash-card-description" style={{ margin: 0 }}>{t('addCard.subtitle')}</p>
        </div>

        {error ? <p className="dash-card-description" role="alert" style={{ margin: 0, color: '#ef4444' }}>{error}</p> : null}

        <div ref={mountRef} style={{ minHeight: ready ? 0 : 120 }} />
        {!ready && !error ? (
          <p className="dash-card-caption" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--dash-muted)' }}>
            <IconLoader2 size={14} className="dash-spin" /> {t('addCard.loading')}
          </p>
        ) : null}

        <p className="dash-card-caption" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--dash-muted)' }}>
          <IconLock size={13} /> {t('addCard.privacy')}
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="dash-secondary-btn" onClick={onClose} disabled={saving}>{t('addCard.cancel')}</button>
          <button
            type="button"
            className="dash-primary-btn"
            onClick={() => void save()}
            disabled={!ready || saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {saving ? <><IconLoader2 size={15} className="dash-spin" /> {t('addCard.saving')}</> : t('addCard.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
